import { InteractionType, CycleData, DragState, InteractionResult } from '../types';
import { ValidationService } from './validation';
import { detectInteraction, calculateNewPosition, createDefaultPermissions } from './interaction';

export interface DragEngineConfig {
	cycles: CycleData[];
	videoDuration: number;
	canvasWidth: number;
	allowBilateral?: boolean;
	isEditingIdentified?: boolean;
	editingCycleId?: string | null;
	allCycles?: CycleData[];
	allowOverlap?: boolean;
}

export interface DragResult {
	cycleId: string;
	start: number;
	end: number;
	isValid: boolean;
	error?: string;
}

export class DragEngine {
	private dragState: DragState = {
		isDragging: false,
		interactionType: InteractionType.NONE,
		cycleId: null,
		dragOffset: 0
	};

	private cycles: CycleData[];
	private videoDuration: number;
	private canvasWidth: number;
	private allowBilateral: boolean;
	private isEditingIdentified: boolean;
	private editingCycleId: string | null;
	private allCycles: CycleData[];
	private allowOverlap: boolean;

	constructor(config: DragEngineConfig) {
		this.cycles = config.cycles;
		this.videoDuration = config.videoDuration;
		this.canvasWidth = config.canvasWidth;
		this.allowBilateral = config.allowBilateral ?? true;
		this.isEditingIdentified = config.isEditingIdentified ?? false;
		this.editingCycleId = config.editingCycleId ?? null;
		this.allCycles = config.allCycles ?? config.cycles;
		this.allowOverlap = config.allowOverlap ?? false;
	}

	updateConfig(config: Partial<DragEngineConfig>): void {
		if (config.cycles !== undefined) this.cycles = config.cycles;
		if (config.videoDuration !== undefined) this.videoDuration = config.videoDuration;
		if (config.canvasWidth !== undefined) this.canvasWidth = config.canvasWidth;
		if (config.allowBilateral !== undefined) this.allowBilateral = config.allowBilateral;
		if (config.isEditingIdentified !== undefined) this.isEditingIdentified = config.isEditingIdentified;
		if (config.editingCycleId !== undefined) this.editingCycleId = config.editingCycleId;
		if (config.allCycles !== undefined) this.allCycles = config.allCycles;
		if (config.allowOverlap !== undefined) this.allowOverlap = config.allowOverlap;
	}

	startDrag(mouseX: number): InteractionResult {
		const permissions = createDefaultPermissions(this.isEditingIdentified, this.editingCycleId);
		const interaction = detectInteraction(mouseX, this.cycles, this.canvasWidth, this.videoDuration, permissions);

		if (interaction.type === InteractionType.NONE || !interaction.cycleId) {
			return interaction;
		}

		const DRAG_INTERACTIONS = [InteractionType.START_RESIZE, InteractionType.END_RESIZE, InteractionType.MOVE];

		if (DRAG_INTERACTIONS.includes(interaction.type)) {
			this.dragState = {
				isDragging: true,
				interactionType: interaction.type,
				cycleId: interaction.cycleId,
				dragOffset: interaction.dragOffset || 0
			};
		}

		return interaction;
	}

	calculateDragPosition(mouseX: number): DragResult | null {
		if (!this.dragState.isDragging || !this.dragState.cycleId) {
			return null;
		}

		const cycle = this.cycles.find((c) => c.id === this.dragState.cycleId);
		if (!cycle) {
			return null;
		}

		const newPosition = calculateNewPosition(
			this.dragState.interactionType,
			mouseX,
			cycle,
			this.dragState.dragOffset,
			this.canvasWidth,
			this.videoDuration,
			this.allCycles
		);

		const validation = ValidationService.canPositionCycle(
			cycle.id,
			newPosition.start,
			newPosition.end,
			this.allCycles,
			this.videoDuration,
			cycle.side,
			this.allowBilateral,
			this.dragState.interactionType === InteractionType.MOVE,
			false,
			this.allowOverlap
		);

		return {
			cycleId: cycle.id,
			start: newPosition.start,
			end: newPosition.end,
			isValid: validation.valid,
			error: validation.error
		};
	}

	endDrag(): void {
		this.dragState = {
			isDragging: false,
			interactionType: InteractionType.NONE,
			cycleId: null,
			dragOffset: 0
		};
	}

	cancelDrag(): void {
		this.endDrag();
	}

	isDragging(): boolean {
		return this.dragState.isDragging;
	}

	getDragState(): Readonly<DragState> {
		return { ...this.dragState };
	}

	getCurrentDragCycleId(): string | null {
		return this.dragState.cycleId;
	}

	getInteractionType(): InteractionType {
		return this.dragState.interactionType;
	}
}
